import {
  normalizeFilter,
  type ColumnFilter,
  type FilterCondition,
  type FilterFieldKind,
} from '@/components/filters/column-filter'
import { messages } from '@/lib/messages'

/**
 * Veri ızgarasının filtre satırı: hücredeki yazı alanı ile `ColumnFilter` (`conditions`) arasındaki
 * eşleme. İş alanı bilgisi yok; hangi sütunun hangi `FilterFieldKind` olduğunu çağıran verir.
 * Başlık menüsü aynı `columnFilters` durumunu yazdığı için satırın gösteremediği (değer listesi,
 * çok koşullu, `startsWith` gibi) filtreler `chip` olarak döner: satır özet çip çizer.
 */

/** Filtre satırı hücresinin türü. */
export type RowFilterKind = 'text' | 'range' | 'none'

/** `text`: "içerir"; `range`: sayı/tarih aralığı (başlangıç–bitiş); `none`: satırda alan yok. */
export function rowFilterKind(kind: FilterFieldKind | undefined): RowFilterKind {
  if (kind === 'text') return 'text'
  if (kind === 'numeric' || kind === 'date' || kind === 'datetime') return 'range'
  return 'none'
}

/** Hücredeki yazılı değerler; `text` için yalnız `text`, `range` için `from`/`to` kullanılır. */
export interface RowFilterDraft {
  text: string
  from: string
  to: string
}

export const EMPTY_ROW_DRAFT: RowFilterDraft = { text: '', from: '', to: '' }

/** Satırın düzenleyemediği filtre: özet çip gösterilir (yerine yazılırsa filtre onun yerine geçer). */
export interface RowFilterChip {
  chip: true
}

/**
 * Uygulanmış filtreden hücre taslağı. Satırın yazabildiği biçimler: metinde tek `contains`; aralıkta
 * `gte` / `lte` / `between` (ya da `gte`+`lte` VE'si). Başka her filtre `chip`.
 */
export function draftFromFilter(
  filter: ColumnFilter | undefined,
  kind: FilterFieldKind | undefined,
): RowFilterDraft | RowFilterChip {
  const mode = rowFilterKind(kind)
  if (filter === undefined) return EMPTY_ROW_DRAFT
  if (mode === 'none' || filter.type !== 'conditions') return { chip: true }
  const { conditions } = filter
  if (mode === 'text') {
    const [only] = conditions
    return conditions.length === 1 && only?.op === 'contains'
      ? { ...EMPTY_ROW_DRAFT, text: only.value }
      : { chip: true }
  }
  const lower = conditions.find((condition) => condition.op === 'gte')
  const upper = conditions.find((condition) => condition.op === 'lte')
  const [only] = conditions
  if (conditions.length === 1 && only?.op === 'between') {
    return { ...EMPTY_ROW_DRAFT, from: only.value, to: only.valueTo ?? '' }
  }
  if (conditions.length === 1 && lower) return { ...EMPTY_ROW_DRAFT, from: lower.value }
  if (conditions.length === 1 && upper) return { ...EMPTY_ROW_DRAFT, to: upper.value }
  if (conditions.length === 2 && lower && upper && filter.join === 'and') {
    return { ...EMPTY_ROW_DRAFT, from: lower.value, to: upper.value }
  }
  return { chip: true }
}

/**
 * Taslaktan filtre. Boş taslak `undefined` (filtre kalkar). Metin: `contains`. Aralık: yalnız başlangıç
 * `gte`, yalnız bitiş `lte`, ikisi `between`. Sonuç `normalizeFilter`den geçer (yarım koşul düşer).
 */
export function filterFromDraft(
  draft: RowFilterDraft,
  kind: FilterFieldKind | undefined,
): ColumnFilter | undefined {
  const mode = rowFilterKind(kind)
  if (mode === 'none' || !kind) return undefined
  let condition: FilterCondition | undefined
  if (mode === 'text') {
    condition = draft.text.trim() === '' ? undefined : { op: 'contains', value: draft.text }
  } else {
    const from = draft.from.trim()
    const to = draft.to.trim()
    if (from !== '' && to !== '') condition = { op: 'between', value: from, valueTo: to }
    else if (from !== '') condition = { op: 'gte', value: from }
    else if (to !== '') condition = { op: 'lte', value: to }
  }
  if (!condition) return undefined
  return normalizeFilter({ type: 'conditions', join: 'and', conditions: [condition] }, kind)
}

/** Yazmayı bırakınca filtre bu kadar sonra adrese yazılır (her tuşta Cube isteği atılmasın). */
export const FILTER_ROW_DEBOUNCE_MS = 400

/** Başlık menüsündeki değer listesi filtresinin ve çok koşullu filtrelerin özeti. */
export function filterSummary(filter: ColumnFilter): string {
  const t = messages.filters.column
  if (filter.type === 'conditions') return t.conditionCount(filter.conditions.length)
  const count = filter.values.length + (filter.blank === (filter.mode === 'in') ? 1 : 0)
  return filter.mode === 'in' ? t.valueCount(count) : t.excludedCount(count)
}
